import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Header } from './components/Header';
import { DropZone } from './components/DropZone';
import { RepairBar } from './components/RepairBar';
import { AnomalyGrid } from './components/AnomalyGrid';
import { CodePanel } from './components/CodePanel';
import { FuzzerPanel } from './components/FuzzerPanel';
import { UpgradeCard } from './components/UpgradeCard';
import { useParser } from './worker/useParser';
import {
  ALL_REPAIRS,
  DEFAULT_REPAIRS,
  type ColumnType,
  type ParseResult,
  type RepairConfig,
  type RepairKey,
} from './lib/types';
import { deriveTableName } from './lib/format';
import { generateCode, type CodeTab } from './lib/generators';
import type { InputFormat } from './lib/pipeline';
import { baseFileName, cleanedToCsv, cleanedToJson, downloadFile } from './lib/exporters';
import {
  clearPro,
  deleteTemplate,
  loadPro,
  loadTemplates,
  savePro,
  saveTemplate,
  type ProState,
  type SchemaTemplate,
} from './lib/storage';
import { DEMO_LICENSE_KEY, isValidLicenseKey } from './lib/license';

interface LoadedSource {
  text: string;
  fileName: string;
  format: InputFormat;
}

export default function App() {
  const { parse, usingWorker } = useParser();

  const [pro, setPro] = useState<ProState | null>(() => loadPro());
  const [templates, setTemplates] = useState<SchemaTemplate[]>(() => loadTemplates());
  const [source, setSource] = useState<LoadedSource | null>(null);
  const [repairs, setRepairs] = useState<RepairConfig>(DEFAULT_REPAIRS);
  const [result, setResult] = useState<ParseResult | null>(null);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [licenseError, setLicenseError] = useState<string | null>(null);
  const [tab, setTab] = useState<CodeTab>('typescript');
  const [tableName, setTableName] = useState('dataset');
  const [overrides, setOverrides] = useState<Record<string, ColumnType>>({});
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const isPro = pro !== null;
  const saveTimer = useRef<number | null>(null);

  /* ----------------------------- parsing ----------------------------- */

  const analyze = useCallback(
    async (next: LoadedSource, repairConfig: RepairConfig, proActive: boolean) => {
      setParsing(true);
      setError(null);
      try {
        const parsed = await parse({
          text: next.text,
          fileName: next.fileName,
          format: next.format,
          repairs: repairConfig,
          isPro: proActive,
        });
        setResult(parsed);
        setTableName(deriveTableName(parsed.fileName));
        setOverrides({});
      } catch (parseError) {
        setError(
          parseError instanceof Error
            ? parseError.message
            : 'Unexpected error while parsing the input.',
        );
      } finally {
        setParsing(false);
      }
    },
    [parse],
  );

  // Re-analyze whenever the source, the repair set or the license changes.
  useEffect(() => {
    if (!source) return;
    void analyze(source, repairs, isPro);
  }, [analyze, source, repairs, isPro]);

  /* ----------------------------- handlers ----------------------------- */

  const handleLoad = useCallback((text: string, fileName: string) => {
    setSource({ text, fileName, format: 'auto' });
    setRepairs(DEFAULT_REPAIRS);
  }, []);

  const handleError = useCallback((message: string) => {
    setError(message);
  }, []);

  const handleToggleRepair = useCallback((key: RepairKey) => {
    setRepairs((current) => ({ ...current, [key]: !current[key] }));
  }, []);

  const handleOverride = useCallback((name: string, type: ColumnType) => {
    setOverrides((current) => ({ ...current, [name]: type }));
  }, []);

  const handleActivate = useCallback((key: string) => {
    const candidate = key.trim() || DEMO_LICENSE_KEY;
    if (!isValidLicenseKey(candidate)) {
      setLicenseError(
        candidate === '' ? 'Enter a license key.' : `“${candidate}” is not a valid ChaosMonkey CSV Pro key.`,
      );
      return;
    }
    const next: ProState = { key: candidate, activatedAt: Date.now() };
    savePro(next);
    setPro(next);
    setLicenseError(null);
  }, []);

  const handleDeactivate = useCallback(() => {
    clearPro();
    setPro(null);
  }, []);

  const scrollToUpgrade = useCallback(() => {
    document.getElementById('upgrade')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const handleSaveTemplate = useCallback(
    (name: string) => {
      if (!result) return;
      const template: SchemaTemplate = {
        id: `tpl_${Date.now().toString(36)}_${Math.floor(Math.random() * 1e6).toString(36)}`,
        name,
        tableName,
        createdAt: Date.now(),
        columns: result.columns.map((column) => ({
          name: column.name,
          key: column.key,
          type: overrides[column.name] ?? column.type,
        })),
      };
      setTemplates(saveTemplate(template));
      setSaveMessage(`Saved template “${name}”.`);
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
      saveTimer.current = window.setTimeout(() => setSaveMessage(null), 2500);
    },
    [result, tableName, overrides],
  );

  const handleApplyTemplate = useCallback((template: SchemaTemplate) => {
    const next: Record<string, ColumnType> = {};
    for (const column of template.columns) next[column.name] = column.type;
    setOverrides(next);
    setSaveMessage(`Applied “${template.name}” — types overridden.`);
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => setSaveMessage(null), 2500);
  }, []);

  const handleDeleteTemplate = useCallback((id: string) => {
    setTemplates(deleteTemplate(id));
  }, []);

  const handleExportCleaned = useCallback(
    (format: 'csv' | 'json') => {
      if (!result) return;
      const base = baseFileName(result.fileName);
      const suffix = result.truncated ? '_cleaned_free' : '_cleaned';
      downloadFile(
        `${base}${suffix}.${format}`,
        format === 'csv' ? cleanedToCsv(result) : cleanedToJson(result),
        format === 'csv' ? 'text/csv' : 'application/json',
      );
    },
    [result],
  );

  /* ----------------------------- derived ----------------------------- */

  const columns = useMemo(() => {
    if (!result) return [];
    return result.columns.map((column) => {
      const type = overrides[column.name];
      return type && type !== column.type ? { ...column, type } : column;
    });
  }, [result, overrides]);

  const code = useMemo(() => {
    if (!result || !columns.length) return '';
    return generateCode(tab, {
      tableName: tableName || 'dataset',
      columns,
      sourceName: result.fileName,
      rowCount: result.rowCount,
      rows: tab === 'postgres' ? result.rows : undefined,
    });
  }, [tab, columns, tableName, result]);

  const anomalies = result ? result.columns.reduce((sum, column) => sum + column.anomalyCount, 0) : 0;
  const cells = result ? result.columns.reduce((sum, column) => sum + column.nullCount, 0) : 0;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      <Header
        isPro={isPro}
        licenseKey={pro?.key ?? null}
        busy={parsing}
        onUpgradeClick={scrollToUpgrade}
      />

      <main className="mx-auto max-w-7xl space-y-6 px-4 pb-16 pt-6">
        {!result && !parsing && <Hero />}

        <DropZone
          busy={parsing}
          activeFileName={result?.fileName ?? null}
          onLoad={handleLoad}
          onError={handleError}
        />

        {error && (
          <div className="flex items-start gap-3 rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-200">
            <svg viewBox="0 0 20 20" className="mt-0.5 h-4 w-4 shrink-0" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm1-11a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm-1 3a1 1 0 0 0-1 1v3a1 1 0 1 0 2 0V11a1 1 0 0 0-1-1Z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <p className="font-medium">Could not analyze that input</p>
              <p className="mt-0.5 text-xs text-rose-300/80">{error}</p>
            </div>
            <button
              type="button"
              onClick={() => setError(null)}
              className="rounded border border-rose-400/30 px-2 py-0.5 text-[11px] text-rose-200/80 hover:bg-rose-500/10"
            >
              dismiss
            </button>
          </div>
        )}

        {result && (
          <>
            <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4 shadow-panel sm:p-5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 rounded-lg bg-slate-950/60 px-2.5 py-1.5 font-mono text-xs text-slate-300">
                  {result.fileName}
                </span>
                <Badge label={result.format} />
                <Badge label={`${result.rowCount.toLocaleString()} rows`} />
                <Badge label={`${result.columns.length} columns`} />
                <Badge label={`${result.parseMs} ms`} />
                <Badge
                  label={`${anomalies.toLocaleString()} anomalies`}
                  tone={anomalies > 0 ? 'rose' : 'lime'}
                />
                <Badge label={`${cells.toLocaleString()} missing cells`} tone="slate" />
                {!usingWorker && (
                  <span className="rounded-full bg-amber-500/10 px-2.5 py-1 text-[10px] font-semibold text-amber-300 ring-1 ring-amber-500/30">
                    worker unavailable — parsed inline
                  </span>
                )}
                <div className="ml-auto flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleExportCleaned('csv')}
                    className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-300 transition hover:border-slate-500 hover:text-white"
                  >
                    Export cleaned .csv
                  </button>
                  <button
                    type="button"
                    onClick={() => handleExportCleaned('json')}
                    className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-300 transition hover:border-slate-500 hover:text-white"
                  >
                    Export cleaned .json
                  </button>
                </div>
              </div>

              {result.truncated && (
                <p className="mt-3 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-200 ring-1 ring-amber-500/20">
                  Free plan: analyzed the first {result.rowCount.toLocaleString()} of{' '}
                  {result.rawRowCount.toLocaleString()} rows. Reprocessing the whole file needs Pro.
                </p>
              )}

              {result.warnings.map((warning) => (
                <p key={warning} className="mt-2 text-[11px] text-slate-500">
                  {warning}
                </p>
              ))}
            </section>

            <RepairBar
              repairs={repairs}
              report={result.repairs}
              busy={parsing}
              onToggle={handleToggleRepair}
              onApplyAll={() => setRepairs(ALL_REPAIRS)}
              onReset={() => setRepairs(DEFAULT_REPAIRS)}
            />

            <AnomalyGrid
              columns={columns}
              headers={result.headers}
              rows={result.rows}
              overrides={overrides}
              onOverride={handleOverride}
            />

            <CodePanel
              tab={tab}
              onTab={setTab}
              code={code}
              tableName={tableName}
              onTableName={setTableName}
              columns={columns}
              isPro={isPro}
              templates={templates}
              onSaveTemplate={handleSaveTemplate}
              onApplyTemplate={handleApplyTemplate}
              onDeleteTemplate={handleDeleteTemplate}
              onUpgrade={scrollToUpgrade}
            />

            <FuzzerPanel
              columns={columns}
              isPro={isPro}
              sourceName={result.fileName}
              onUpgrade={scrollToUpgrade}
            />

            {saveMessage && (
              <div className="fixed bottom-4 left-1/2 z-30 -translate-x-1/2 rounded-full bg-lime-500 px-4 py-2 text-xs font-semibold text-slate-950 shadow-xl">
                {saveMessage}
              </div>
            )}
          </>
        )}

        <UpgradeCard
          isPro={isPro}
          licenseKey={pro?.key ?? null}
          licenseError={licenseError}
          rowCount={result?.rowCount ?? 0}
          rawRowCount={result?.rawRowCount ?? 0}
          truncated={result?.truncated ?? false}
          onActivate={handleActivate}
          onDeactivate={handleDeactivate}
        />

        <Footer />
      </main>
    </div>
  );
}

function Hero() {
  const bullets = [
    ['Audit', 'Infer column types, flag every outlier cell, and see exactly which rows broke your importer.'],
    ['Repair', 'One click applies deterministic repairs: trim, ISO-8601 dates, currency stripping, null standardization.'],
    ['Generate', 'Instant TypeScript, Zod, Pydantic v2 and PostgreSQL from the inferred schema.'],
    ['Break it', 'Generate a 50-row chaos dataset of SQLi, XSS, ZWJ emoji, RTL text and epoch-boundary dates, then export it.'],
  ];
  return (
    <section className="rounded-2xl border border-slate-800 bg-gradient-to-b from-slate-900/80 to-slate-900/30 p-6 shadow-panel sm:p-8">
      <h2 className="text-xl font-semibold tracking-tight text-slate-100 sm:text-2xl">
        Know what your CSV will do to your importer — <span className="text-amber-400">before</span> it does it.
      </h2>
      <p className="mt-2 max-w-2xl text-sm text-slate-400">
        ChaosMonkey CSV audits messy CSV / JSON inside a Web Worker, infers a production-ready schema, and then
        tortures it with the payloads that usually break imports. Zero uploads, zero backend, zero telemetry.
      </p>
      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {bullets.map(([title, body]) => (
          <div key={title} className="rounded-xl border border-slate-800 bg-slate-950/50 p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-amber-400">{title}</p>
            <p className="mt-1.5 text-[12px] leading-relaxed text-slate-400">{body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Badge({ label, tone = 'slate' }: { label: string; tone?: 'slate' | 'rose' | 'lime' }) {
  const tones: Record<string, string> = {
    slate: 'bg-slate-800/70 text-slate-300 ring-slate-700',
    rose: 'bg-rose-500/15 text-rose-300 ring-rose-500/30',
    lime: 'bg-lime-500/15 text-lime-300 ring-lime-500/30',
  };
  return (
    <span
      className={`rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ${tones[tone] ?? tones.slate}`}
    >
      {label}
    </span>
  );
}

function Footer() {
  return (
    <footer className="border-t border-slate-800/80 pt-5 text-[11px] leading-relaxed text-slate-600">
      <p>
        100% local: files are parsed in a Web Worker in this tab and never leave your device. Installable PWA —
        works offline after the first load.
      </p>
      <p className="mt-1">
        Vite · TypeScript · React · Tailwind CSS · Web Workers · deterministic seedable fuzzing.
      </p>
    </footer>
  );
}
